import React from 'react';
import ErrorPage from '../ErrorPage';
import { reportBoundaryError, ERROR_BOUNDARY_NAMES } from '../../utils/errorReporting';

const WEBPACK_ERROR_MARKER = '__webpack_require__';
const WEBPACK_ERROR_REFRESH_KEY = 'webpackErrorRefreshCount';
const MAX_WEBPACK_REFRESH_ATTEMPTS = 3;
const REFRESH_COUNTER_TTL_MS = 30000;

const getRefreshKey = () => `${WEBPACK_ERROR_REFRESH_KEY}_${window.location.pathname}`;
const getRefreshCount = () => parseInt(sessionStorage.getItem(getRefreshKey()) || '0', 10);

class LazyErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, isWebpackError: false };
    }

    static getDerivedStateFromError(error) {
        const isWebpackError = !!error?.message?.includes(WEBPACK_ERROR_MARKER);
        return { hasError: true, isWebpackError };
    }

    componentDidCatch(error, errorInfo) {
        const { isWebpackError } = this.state;
        reportBoundaryError(error, errorInfo, {
            boundary: ERROR_BOUNDARY_NAMES.LAZY_ROUTE,
            isWebpackError,
        });

        if (isWebpackError) {
            this.refreshAfterWebpackError(error);
        }
    }

    refreshAfterWebpackError(error) {
        const currentPath = window.location.pathname;
        const refreshKey = getRefreshKey();
        const refreshCount = getRefreshCount();

        if (refreshCount < MAX_WEBPACK_REFRESH_ATTEMPTS) {
            sessionStorage.setItem(refreshKey, (refreshCount + 1).toString());
            setTimeout(() => {
                sessionStorage.removeItem(refreshKey);
            }, REFRESH_COUNTER_TTL_MS);

            if (window.gtag) {
                window.gtag('event', 'webpack_error_auto_refresh', {
                    event_category: 'error',
                    event_label: currentPath,
                    custom_parameter_1: error.message,
                    custom_parameter_2: refreshCount + 1,
                });
            }

            window.location.reload();
        } else if (window.gtag) {
            window.gtag('event', 'webpack_error_max_refresh_reached', {
                event_category: 'error',
                event_label: currentPath,
                custom_parameter_1: error.message,
            });
        }
    }

    render() {
        const { hasError, isWebpackError } = this.state;

        if (!hasError) {
            return <>{this.props.children}</>;
        }

        if (isWebpackError && getRefreshCount() < MAX_WEBPACK_REFRESH_ATTEMPTS) {
            return (
                <div className="d-flex justify-content-center align-items-center flex-column" style={{ height: '300px' }}>
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="sr-only" data-testid="routes.webpack-error.loading-text">Loading...</span>
                    </div>
                    <h5 data-testid="routes.webpack-error.loading-title">Loading...</h5>
                </div>
            );
        }

        return (
            <ErrorPage
                subtitle="Something went wrong loading this page"
                showReloadButton={true}
                showBackButton={false}
                onReload={() => {
                    sessionStorage.removeItem(getRefreshKey());
                    window.location.reload();
                }}
                data-testid="routes.error-page"
            />
        );
    }
}

export default LazyErrorBoundary;
